// Define whitelist of allowed Google Fonts
const ALLOWED_FONTS = [
  'Geist',
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Source Sans Pro',
  'Raleway',
  'Nunito',
  'Ubuntu',
  'Work Sans',
  'Playfair Display',
  'Merriweather',
  'Oswald',
  'Roboto Condensed',
];

// Cache to track loaded fonts
const loadedFonts = new Set();

const useGoogleFonts = () => {
  /**
   * Loads a Google Font dynamically
   * @param {string} fontFamily - Font family name (must be in whitelist)
   * @param {string[]} weights - Array of font weights (e.g., ['400', '500', '700'])
   * @returns {Promise<void>}
   */
  const loadGoogleFont = async (fontFamily, weights = ['400', '500', '700']) => {
    // Validate font is in whitelist
    if (!ALLOWED_FONTS.includes(fontFamily)) {
      // Font not in whitelist - silently fail
      return Promise.resolve();
    }

    // Check if already loaded
    const fontKey = `${fontFamily}:${weights.join(',')}`;
    if (loadedFonts.has(fontKey)) {
      return Promise.resolve();
    }

    // Build Google Fonts URL
    const fontName = fontFamily.replace(/\s+/g, '+');
    const weightsParam = weights.join(';');
    const url = `https://fonts.googleapis.com/css2?family=${fontName}:wght@${weightsParam}&display=swap`;

    // Create and inject link tag
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.media = 'print';
      link.onload = () => {
        link.media = 'all';
        loadedFonts.add(fontKey);
        resolve();
      };
      link.onerror = reject;
      document.head.appendChild(link);
    });
  };

  /**
   * Applies a font family to the document root
   * @param {string} fontFamily - Font family name
   */
  const applyFontFamily = (fontFamily) => {
    if (!fontFamily) return;

    const root = document.documentElement;
    // Fallback to system fonts if font fails to load
    root.style.setProperty(
      '--font-family',
      `'${fontFamily}', Arial, Helvetica, sans-serif`,
    );
    root.style.setProperty(
      '--font-family-variable',
      `'${fontFamily}', Arial, Helvetica, sans-serif`,
    );
  };

  /**
   * Loads and applies a Google Font
   * @param {string} fontFamily - Font family name
   * @param {string[]} weights - Array of font weights
   */
  const loadAndApplyFont = async (fontFamily, weights = ['400', '500', '700']) => {
    if (!fontFamily || fontFamily.trim() === '' || fontFamily === 'Rubik') {
      // Keep default Rubik if no font specified
      return;
    }

    try {
      await loadGoogleFont(fontFamily, weights);
      applyFontFamily(fontFamily);
    } catch (error) {
      // Failed to load font - fallback to Rubik on error
      const root = document.documentElement;
      root.style.setProperty('--font-family', "'Rubik', Arial, Helvetica, sans-serif");
      root.style.setProperty('--font-family-variable', "'Rubik-Variable', Arial, Helvetica, sans-serif");
    }
  };

  return {
    loadGoogleFont,
    applyFontFamily,
    loadAndApplyFont,
    ALLOWED_FONTS,
  };
};

export default useGoogleFonts;
